/**
 * FaxUsageWidget
 * Shows month-to-date fax usage: pages sent, cost projection, and — if the
 * agency is on eFax and would save meaningfully — a friendly savings prompt.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { TrendingDown, FileText, DollarSign, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FaxUsageWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/fax/usage-stats`)
      .then((res) => {
        if (mounted) setStats(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading || !stats) return null;
  const providerLabel = stats.active_provider === "srfax" ? "SRFax" : "eFax";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={<FileText className="w-4 h-4" />}
          label="Pages this month"
          value={stats.total_pages_mtd}
          testid="fax-usage-pages"
        />
        <MetricCard
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          label="Sent successfully"
          value={stats.sent_count}
          testid="fax-usage-sent"
        />
        <MetricCard
          icon={<AlertCircle className="w-4 h-4 text-red-500" />}
          label="Failed"
          value={stats.failed_count}
          testid="fax-usage-failed"
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4 text-slate-600" />}
          label={`Est. ${providerLabel} cost`}
          value={`$${stats.cost_estimates.current_month_projected.toFixed(2)}`}
          testid="fax-usage-cost"
        />
      </div>

      {stats.show_savings_prompt && (
        <Card className="bg-emerald-50 border-emerald-200" data-testid="fax-savings-prompt">
          <CardContent className="py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <TrendingDown className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-emerald-900">
                  You could save an estimated $
                  {stats.cost_estimates.projected_annual_savings.toFixed(0)}/year
                  switching to SRFax
                </p>
                <p className="text-xs text-emerald-800 mt-0.5">
                  Based on {stats.total_pages_mtd} pages this month. Setup takes
                  about 10 minutes and is HIPAA compliant.
                </p>
              </div>
              <Link to="/settings?tab=integrations">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Explore SRFax
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="py-3">
        <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
          {icon}
          {label}
        </div>
        <div className="text-xl font-semibold text-slate-800 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
